import { ReactNode } from 'react';
import { toast } from 'sonner';
import { registrarTentativaLigacao } from '@/lib/leadHistoricoService';
import { useAuth } from '@/hooks/useAuth';
import { formatarTelefone } from '@/utils/formatters';
import { formatPhoneForCall } from '@/utils/phone';
import { useSoftphoneOptional } from '@/components/softphone/SoftphoneProvider';

interface ClickToCallProps {
  /** Número de telefone a ser discado */
  telefone: string;
  /** ID do lead para registrar histórico (opcional) */
  leadId?: string;
  /** Nome do contato a exibir no softphone (opcional) */
  nome?: string;
  /** Conteúdo a ser renderizado (geralmente um botão ou ícone) */
  children: ReactNode;
  /** Classe CSS adicional */
  className?: string;
  /**
   * Callback do clique. ⚠️ Em 3 telas (LeadsTableColumns, ModalDetalhesLeadCompleto
   * e LeadDados360Portabilidade) esse callback é o `onLigar` da página, que JÁ
   * disca pelo softphone. Por isso, quando `onClick` é passado, este componente
   * NÃO disca também — senão sairiam duas chamadas para o mesmo número no mesmo
   * clique. O registro no histórico do lead continua acontecendo aqui.
   */
  onClick?: () => void;
}

/**
 * Todo "Ligar" do CRM passa por aqui — e SEMPRE sai pelo softphone da Prime.
 *
 * ⛔ NÃO devolver o `href="tel:"` nem o `window.open('tel:')`. Douglas 06/08:
 * "eu quero o uso manual exclusivo do nosso softphone, não quero deixar
 * externo". Até hoje o componente era um `<a href="tel:...">`: quando o
 * softphone não estava registrado, o navegador abria o discador do computador
 * em silêncio. A pessoa falava com o cliente achando que tinha ligado pelo CRM,
 * e a ligação ficava sem gravação e sem registro. Medido em 06/08: no dia
 * inteiro, só 7 ligações manuais passaram pelo softphone.
 *
 * ⛔ E NÃO colocar `onClick` com `stopPropagation` no filho: o handler do filho
 * roda antes deste e mata a discagem (bug da Giovana, 05/08).
 */
export function ClickToCall({
  telefone,
  leadId,
  nome,
  children,
  className = '',
  onClick,
}: ClickToCallProps) {
  const { user } = useAuth();
  const softphone = useSoftphoneOptional();

  const numeroFormatado = formatPhoneForCall(telefone);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!numeroFormatado) {
      toast.error('Telefone inválido', { description: 'Este contato não tem um número válido para ligar.' });
      return;
    }

    if (!softphone) {
      toast.error('A ligação NÃO foi feita — softphone indisponível nesta tela', {
        description: 'Recarregue a página (Ctrl+Shift+R). Ligar por fora do CRM não fica gravado.',
        duration: 9000,
      });
      return;
    }

    // Quem passou onClick já disca por conta própria (ver comentário na prop):
    // discar aqui também geraria DUAS chamadas no mesmo clique.
    if (onClick) {
      onClick();
    } else {
      softphone.call(numeroFormatado, nome);
    }

    if (softphone.status === 'registrado' && leadId && user?.id) {
      try {
        await registrarTentativaLigacao(leadId, user.id, formatarTelefone(numeroFormatado));
      } catch (err) {
        console.error('Erro ao registrar tentativa de ligação:', err);
      }
    }
  };

  // <span>, não <button>: os 21 pontos de uso já passam um <Button> como filho,
  // e botão dentro de botão é HTML inválido. Quem tem foco de teclado e semântica
  // é o filho — este wrapper só intercepta o clique que sobe dele.
  return (
    <span className={className} onClick={handleClick}>
      {children}
    </span>
  );
}
